/* eslint-disable no-fallthrough */
import React from 'react';
import { IntlShape } from 'react-intl';
import { SerializedMeter } from '@enmon/commons/lib/enmon';
import { MeterFileValue } from '@enmon/commons/lib/enmon/files/values';

import { Commodity } from '@enmon/commons/lib/shared';
import { isThermometer } from '../../../../utils/isThermometer';
import { InstallationPhotoMessages } from '../../messages';

export interface PhotoConfigShape {
  help: React.ReactNode;
  label: string;
  formItemName: MeterFileValue | string;
  required?: boolean;
}

export const getPackageLabelConfig = (intl: IntlShape): PhotoConfigShape => ({
  help: intl.formatMessage(InstallationPhotoMessages.packageLabelHelp),
  label: intl.formatMessage(InstallationPhotoMessages.packageLabelLabel),
  formItemName: MeterFileValue.PackageLabel,
});

export const getOpenSensorConfig = (intl: IntlShape): PhotoConfigShape => ({
  help: intl.formatMessage(InstallationPhotoMessages.openSensorHelp),
  label: intl.formatMessage(InstallationPhotoMessages.openSensorLabel),
  formItemName: MeterFileValue.OpenSensor,
});

export const getClosedSensorConfig = (intl: IntlShape): PhotoConfigShape => ({
  help: intl.formatMessage(InstallationPhotoMessages.closedSensorHelp),
  label: intl.formatMessage(InstallationPhotoMessages.closedSensorLabel),
  formItemName: MeterFileValue.ClosedSensor,
});

export const getImpulseConstantConfig = (intl: IntlShape): PhotoConfigShape => ({
  help: intl.formatMessage(InstallationPhotoMessages.impulseConstantHelp),
  label: intl.formatMessage(InstallationPhotoMessages.impulseConstantLabel),
  formItemName: MeterFileValue.ImpulseConstant,
});

export const getConnectionDetailConfig = (intl: IntlShape): PhotoConfigShape => ({
  help: intl.formatMessage(InstallationPhotoMessages.connectionDetailHelp),
  label: intl.formatMessage(InstallationPhotoMessages.connectionDetailLabel),
  formItemName: MeterFileValue.ConnectionDetail,
  required: false,
});

export const getPlacementOverviewConfig = (intl: IntlShape): PhotoConfigShape => ({
  help: intl.formatMessage(InstallationPhotoMessages.placementOverviewHelp),
  label: intl.formatMessage(InstallationPhotoMessages.placementOverviewLabel),
  formItemName: MeterFileValue.PlacementOverview,
  required: false,
});

/** Returns config for PhotoForm based on commodity */
export const getPhotoConfigs = (intl: IntlShape, meter: SerializedMeter): PhotoConfigShape[] => {
  const DEFAULT_PHOTO_CONFIGS = [
    getPackageLabelConfig(intl),
    getOpenSensorConfig(intl),
    getClosedSensorConfig(intl),
    getImpulseConstantConfig(intl),
    getConnectionDetailConfig(intl),
    getPlacementOverviewConfig(intl),
  ];

  const THERMOMETER_PHOTO_CONFIGS = [
    getPackageLabelConfig(intl),
    getClosedSensorConfig(intl),
    getPlacementOverviewConfig(intl),
  ];

  switch (meter.commodity) {
    case Commodity.Other:
      if (isThermometer(meter.detail)) return THERMOMETER_PHOTO_CONFIGS;

    default:
      return DEFAULT_PHOTO_CONFIGS;
  }
};
